import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import './AllNews.css';
import Header from '../Header/Header.js';
import Footer from '../Footer/Footer.js';
import FilterNewsButton from '../FilterNewsButton/FilterNewsButton.jsx';
import NewsLoader from '../NewsLoader/NewsLoader.js';
import { getApiBaseUrl } from '../../utils/api.js';
import { getNewsCover, getNewsPreview, getNewsYear } from '../../utils/news.js';

const newsYears = ['2026', '2025', '2024', '2023', '2022', '2021', '2020', '2019'];
const newsPageSize = 10;

function getNewsCountLabel(newsCount) {
  return `${newsCount} ${
    newsCount === 1 ? 'материал' : newsCount < 5 && newsCount > 1 ? 'материала' : 'материалов'
  }`;
}

function getNewsListFromResponse(data) {
  if (Array.isArray(data)) {
    return {
      items: data,
      total: data.length,
    };
  }

  return {
    items: Array.isArray(data.items) ? data.items : [],
    total: Number.isInteger(data.total) ? data.total : 0,
  };
}

function getNewsPageUrl(year, offset) {
  const searchParams = new URLSearchParams({
    year,
    limit: String(newsPageSize),
    offset: String(offset),
  });

  return `${getApiBaseUrl()}/api/news?${searchParams.toString()}`;
}

function AllNews() {
  const [news, setNews] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [activeYear, setActiveYear] = useState(newsYears[0]);
  const [totalNewsCount, setTotalNewsCount] = useState(0);

  useEffect(() => {
    let isCurrentRequest = true;

    setIsLoading(true);
    setNews([]);
    setTotalNewsCount(0);

    fetch(getNewsPageUrl(activeYear, 0))
      .then((res) => {
        if (!res.ok) {
          throw new Error('Ошибка загрузки новостей');
        }

        return res.json();
      })
      .then((data) => {
        if (!isCurrentRequest) {
          return;
        }

        const { items, total } = getNewsListFromResponse(data);

        setNews(items);
        setTotalNewsCount(total);
      })
      .catch((err) => {
        if (isCurrentRequest) {
          console.error(err);
          setNews([]);
          setTotalNewsCount(0);
        }
      })
      .finally(() => {
        if (isCurrentRequest) {
          setIsLoading(false);
        }
      });

    return () => {
      isCurrentRequest = false;
    };
  }, [activeYear]);

  const hasMoreNews = news.length < totalNewsCount;
  const newsCountLabel = getNewsCountLabel(totalNewsCount);

  function handleFilterButton(year) {
    setActiveYear(year);
  }

  function handleLoadMoreNews() {
    setIsLoadingMore(true);

    fetch(getNewsPageUrl(activeYear, news.length))
      .then((res) => {
        if (!res.ok) {
          throw new Error('Ошибка загрузки новостей');
        }

        return res.json();
      })
      .then((data) => {
        const { items, total } = getNewsListFromResponse(data);

        setNews((currentNews) => [...currentNews, ...items]);
        setTotalNewsCount(total);
      })
      .catch((err) => console.error(err))
      .finally(() => setIsLoadingMore(false));
  }

  return (
    <>
      <Header />
      <main className="content">
        <section className="allNews">
          <section className="allNews__hero">
            <p className="allNews__eyebrow">Медиацентр</p>
            <h2 className="allNews__title">Новости</h2>
            <p className="allNews__lead">
              Актуальные события Самарского областного отделения ВДПО,
              профильные мероприятия, проекты и публикации по годам.
            </p>
          </section>

          <section className="allNews__toolbar">
            <div className="allNews__toolbar-heading">
              <p className="allNews__toolbar-title">Выберите год публикации</p>
              <p className="allNews__toolbar-subtitle">
                {activeYear} • {isLoading ? 'загрузка...' : newsCountLabel}
              </p>
            </div>
            <ul className="allNews__filter-container">
              {newsYears.map((year) => (
                <FilterNewsButton
                  key={year}
                  activeButton={activeYear}
                  onClick={handleFilterButton}
                  buttonText={year}
                />
              ))}
            </ul>
          </section>

          {isLoading ? (
            <section className="allNews__loading">
              <NewsLoader text="Загрузка списка новостей..." />
            </section>
          ) : news.length === 0 ? (
            <div className="allNews__empty-container">
              <p className="allNews__empty-title">За {activeYear} год материалы не опубликованы</p>
              <p className="allNews__empty-text">
                Можно перейти в архив или выбрать другой год в фильтре.
              </p>
              <Link to="/all-news/archive/" className="allNews__button-all-news">
                Архив
              </Link>
            </div>
          ) : (
            <>
              <div className="allNews__container">
                {news.map((item) => (
                  <article className="allNews__container-card" key={item._id}>
                    <Link
                      to={`/news/${item._id}/`}
                      className="allNews__container-card-link"
                    >
                      <div className="allNews__container-card-media">
                        <img
                          src={getNewsCover(item)}
                          alt={item.title}
                          className="allNews__container-card-img"
                        />
                        <span className="allNews__container-card-badge">
                          {getNewsYear(item) || activeYear}
                        </span>
                      </div>
                      <div className="allNews__container-card-content">
                        <p className="allNews__container-card-date">
                          {item.date}
                        </p>
                        <h3 className="allNews__container-card-title">
                          {item.title}
                        </h3>
                        <p className="allNews__container-card-preview">
                          {getNewsPreview(item)}
                        </p>
                        <span className="allNews__container-card-action">
                          Подробнее
                        </span>
                      </div>
                    </Link>
                  </article>
                ))}
              </div>
              {hasMoreNews ? (
                <button
                  type="button"
                  className="allNews__load-more-button"
                  onClick={handleLoadMoreNews}
                  disabled={isLoadingMore}
                >
                  {isLoadingMore ? 'Загружаем...' : 'Загрузить еще'}
                </button>
              ) : null}
            </>
          )}
        </section>
      </main>
      <Footer />
    </>
  );
}

export default AllNews;
